<template>
  <v-card class="pa-0 ma-0">
    <v-card-title class="pa-0 pt-4 px-6">
      {{ $t('people.add_users') }}
    </v-card-title>
    <v-card-text class="pa-0 mx-0 px-6">
      <v-form v-model="isFormValid" ref="addUserForm">
        <p class="add-user__error mb-0" v-if="!canAddUsers">
          <span v-if="!canAddUsers">{{
            $t('people.talk_to_support_to_upgrade')
          }}</span>
        </p>
        <p
          class="mb-0 mt-4 text-body-2 font-weight-bold blue-grey--text text--darken-3"
        >
          {{ $t('people.set_up') }}
        </p>
        <LocationSelector
          data-tourid="add-user-invite-tags"
          key="add-user-invite-tags"
          :organizationId="organizationId"
          :location="form.location"
          @change="locationSelectorChange"
        />

        <v-row class="ma-0 pa-0">
          <v-col cols="12" sm="12" md="6" class="ma-0 pa-0">
            <TeamSelector
              class="me-3"
              :disabled="isUploading"
              :organizationId="organizationId"
              :siteId="form.location.site"
              :selectedTeamId="form.selectedTeam"
              @change="form.selectedTeam = $event"
              @click.native="
                trackAddPeopleNavigate({
                  element: 'teamsSelector',
                  section: 'setup',
                })
              "
            />
          </v-col>
        </v-row>
        <v-row class="ma-0 pa-0 mt-4" v-if="organizationAuthIsLocal">
          <v-col class="ma-0 pa-0" cols="12">
            <div class="d-flex align-center mb-4">
              <p
                class="mb-0 text-body-2 font-weight-bold blue-grey--text text--darken-3"
              >
                {{ $t('common.temporary_password') }}
              </p>
              <v-tooltip top max-width="284px">
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-on="on"
                    class="ms-2"
                    size="20px"
                    style="margin-top: 1px !important"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                {{ $t('common.temporary_password_description') }}
              </v-tooltip>
            </div>
          </v-col>
        </v-row>
        <AddPassword
          v-if="organizationAuthIsLocal"
          singleRow
          :password="form.password"
          :confirmPassword="form.confirmPassword"
          :isLoading="isUploading"
          @change="handlePasswordChange"
        />
        <p
          class="mb-4 mt-6 text-body-2 font-weight-bold blue-grey--text text--darken-3"
        >
          {{ $t('common.users.user_other') }}
        </p>
        <UserForm
          v-for="(user, index) in accountsToInvite"
          :canAssignRole="canAssignRole"
          :account="user"
          :defaultSiteId="form.location.site"
          :key="index"
          :index="index + 1"
          :rolesEnabled="rolesEnabled"
          :showRemove="accountsToInvite.length > 1"
          :active="index === activeIndex"
          :sites="targetOrgSites"
          :siteGroups="targetOrgSiteGroups"
          @change="changeUser($event, index)"
          @remove="removeRow(index)"
          @click.native="activeIndex = index"
        />
        <v-btn
          outlined
          small
          color="primary"
          rounded
          data-testid="onboardingInvite-button-addAnother"
          class="label"
          :disabled="!showAddMoreButton"
          @click="addRow"
        >
          <v-icon left size="16"> mdi-plus </v-icon
          >{{ $t('onboarding.invite.add_another') }}</v-btn
        >
      </v-form>
    </v-card-text>

    <v-card-actions class="justify-space-between pa-0 ma-0 mt-8 pb-4 px-6">
      <div>
        <Button
          plain
          class="me-2"
          color="grey"
          data-testid="adduserModal-button-cancel"
          data-tourid="cancel-invite-button"
          :text="$t('common.button.back')"
          @click="handleBack"
        />
      </div>
      <div>
        <Button
          plain
          class="me-2"
          color="grey"
          data-testid="adduserModal-button-cancel"
          data-tourid="cancel-invite-button"
          :text="$t('common.button.cancel')"
          @click="handleCancel"
        />

        <Button
          color="primary"
          data-testid="addUserModal-button-sendInvite"
          data-tourid="send-invite-button"
          :loading="isUploading"
          :disabled="isUploading"
          :text="$t('common.button.add')"
          @click="onSubmit"
        />
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import _ from 'lodash'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import api from '@/api'

import Button from '@/components/UI/Button'
import LocationSelector from '@/components/LocationSelector/LocationSelector.vue'
import AddPassword from '@/components/AddPassword.vue'
import TeamSelector from './TeamSelectorAddBulk.vue'
import UserForm from '@/components/People/UserForm.vue'

const DEFAULT_ACCOUNT_RECORD = Object.freeze({
  email: '',
  displayname: '',
  familyname: '',
  position: '',
  isOrganizationAdmin: false,
  isSiteAdmin: false,
  isRoutineManager: false,
  isCoach: false,
  canDoFinancialValidation: false,
  managedSites: [],
  managedSiteGroups: [],
})

export default {
  name: 'AddUserForm',

  mixins: [mixin, trackerMixin],

  components: {
    LocationSelector,
    Button,
    AddPassword,
    TeamSelector,
    UserForm,
  },

  props: {
    remainingUsersToAdd: {
      type: Number,
      required: true,
    },

    organizationId: {
      type: String,
      required: true,
    },

    rolesEnabled: {
      type: Array,
      required: false,
      default() {
        return ['isCoach']
      },
    },
    canAssignRole: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      organization: {},
      organizationStructure: null,
      accountsToInvite: [{ ...DEFAULT_ACCOUNT_RECORD }],
      isFormValid: null,
      isUploading: false,
      activeIndex: -1,
      form: {
        password: '',
        confirmPassword: '',
        selectedTeam: '',
        location: {
          organization: '',
          site: '',
          tags: [],
        },
      },
    }
  },

  async mounted() {
    await this.getOrganization()

    if (!this.isOwnOrganization) {
      await this.fetchOrganizationStructure()
      return
    }

    this.form.location = this.accountLocation
  },

  computed: {
    ...mapGetters(['account']),

    isOwnOrganization() {
      return this.organizationId === this.accountLocation.organization
    },

    targetOrgSites() {
      return this.isOwnOrganization
        ? null
        : this.organizationStructure?.sites ?? []
    },

    targetOrgSiteGroups() {
      return this.isOwnOrganization
        ? null
        : this.organizationStructure?.siteGroups ?? []
    },

    organizationAuthIsLocal() {
      return this.organization?.authenticationType?.includes('local')
    },

    accountLocation() {
      return {
        organization: this.account?.organization?._id,
        site: this.account?.site?._id,
        tags: [],
      }
    },

    canAddUsers() {
      return this.accountsToInvite.length <= this.remainingUsersToAdd
    },

    showAddMoreButton() {
      return this.accountsToInvite.length <= this.remainingUsersToAdd - 1
    },
  },

  methods: {
    ...mapActions({
      createAccount: 'createAccount',
    }),

    handlePasswordChange(password) {
      this.form = { ...this.form, ...password }
    },

    resetForm() {
      this.resetRows()
    },

    handleCancel() {
      this.$emit('closeModal')
      this.resetRows()
    },

    async getOrganization() {
      try {
        this.isLoading = true

        const organizationToFetch =
          this.organizationId ?? this.accountLocation.organization
        const response = await api.organization.getById(organizationToFetch)

        this.organization = response.data
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async fetchOrganizationStructure() {
      try {
        const response = await api.organization.getOrganizationStructure(
          this.organizationId
        )
        this.organizationStructure = response.data
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    locationSelectorChange(data) {
      this.form.location = {
        ...data,
        organization: this.accountLocation.organization,
      }
    },

    addRow() {
      this.trackAddPeopleNavigate({
        element: 'addAnother',
        section: 'addManuallyModal',
      })
      this.accountsToInvite = [
        ...this.accountsToInvite,
        { ...DEFAULT_ACCOUNT_RECORD },
      ]
    },

    removeRow(index) {
      this.accountsToInvite.splice(index, 1)
    },

    resetRows() {
      this.form.location = this.accountLocation
      this.form.selectedTeam = ''
      this.form.password = ''
      this.form.confirmPassword = ''
      this.accountsToInvite = [{ ...DEFAULT_ACCOUNT_RECORD }]
    },

    async onSubmit() {
      this.$refs.addUserForm.validate()
      await this.$nextTick()

      if (!this.isFormValid || !this.canAddUsers) return

      this.trackUserCreation()
      await this.createAccounts()
      this.$emit('onUsersAdded', this.accountsToInvite)
      this.$emit('closeModal')
      this.resetRows()
    },

    changeUser(field, index) {
      const newArray = _.cloneDeep(this.accountsToInvite)

      newArray[index] = {
        ...this.accountsToInvite[index],
        ...field,
      }

      this.accountsToInvite = _.cloneDeep(newArray)
    },

    trackUserCreation() {
      const totalUsers = this.accountsToInvite.length
      const usersWithJobPosition = this.accountsToInvite.filter(
        user => user?.position?.length
      )
      this.trackAddPeople({
        totalUsers,
        usersWithJobPosition: usersWithJobPosition.length,
      })
    },

    async createAccounts() {
      this.isUploading = true
      const errors = []
      for (const account of this.accountsToInvite) {
        try {
          const payload = {
            organizationId: this.organizationId,
            account: {
              organization: this.organizationId,
              email: account.email,
              displayname: account.displayname,
              familyname: account.familyname,
              position: account.position,
              site: this.form.location.site,
              tags: this.form.location.tags,
              isCoach: account.isCoach,
              canDoFinancialValidation: account.canDoFinancialValidation,
              isOrganizationAdmin: account.isOrganizationAdmin,
              isSiteAdmin: account.isSiteAdmin,
              isRoutineManager: account.isRoutineManager,
              password: this.form.password,
              teams: this.form.selectedTeam ? [this.form.selectedTeam] : [],
              employeeId: account.employeeId,
              country: this.form.location.country,
              managedSites: this.getManagedSitesForPayload(account),
              managedSiteGroups: this.getManagedSiteGroupsForPayload(account),
            },
          }

          await this.createAccount(payload)
        } catch (error) {
          errors.push({ account, error })
        }
      }

      this.isUploading = false
      this.manageCreationResult(errors, this.accountsToInvite.length)
    },

    manageCreationResult(errors, totalUsersAdded) {
      if (errors.length > 0) {
        return this.showError(this.$t('people.account_invitation_error'))
      }

      this.showSuccess(
        this.$t('onboarding.invite.successfully_sent_invitations')
      )

      this.$track(
        'organization.Update',
        this.$store,
        {
          change: 'addUsers',
          page: 'people',
          totalUsersAdded,
        },
        this.trackInIntecom
      )
    },

    handleBack() {
      this.resetForm()
      this.$emit('clickBack')
    },

    getManagedSitesForPayload(account) {
      if (!account.isSiteAdmin) return []

      return account.managedSites ?? []
    },

    getManagedSiteGroupsForPayload(account) {
      if (!account.isSiteAdmin) return []

      return account.managedSiteGroups ?? []
    },
  },
}
</script>

<style lang="scss" scoped>
.add-user__error {
  font-size: $font-size-12;
  color: $color-red;
}
</style>
